import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, Plus, Trash2, Pencil, Database, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading font-extrabold text-3xl flex items-center gap-2">
          <Users className="h-7 w-7 text-primary" /> Administrasi
        </h1>
        <p className="text-muted-foreground mt-1">Kelola pengguna, data referensi, dan konfigurasi skoring.</p>
      </div>
      <Tabs defaultValue="users">
        <TabsList>
          <TabsTrigger value="users" data-testid="tab-users"><Users className="h-4 w-4 mr-1.5" /> Pengguna</TabsTrigger>
          <TabsTrigger value="reference" data-testid="tab-reference"><Database className="h-4 w-4 mr-1.5" /> Data Referensi</TabsTrigger>
          <TabsTrigger value="scoring" data-testid="tab-scoring"><SlidersHorizontal className="h-4 w-4 mr-1.5" /> Skoring</TabsTrigger>
        </TabsList>
        <TabsContent value="users" className="mt-4"><UsersTab /></TabsContent>
        <TabsContent value="reference" className="mt-4"><ReferenceTab /></TabsContent>
        <TabsContent value="scoring" className="mt-4"><ScoringTab /></TabsContent>
      </Tabs>
    </div>
  );
}

const ROLE_LABEL = { student: "Mahasiswa", lecturer: "Dosen", admin: "Admin" };

function UsersTab() {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const { data: users = [] } = useQuery({ queryKey: ["admin-users"], queryFn: () => api.get("/admin/users").then((r) => r.data) });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "student", nim: "" });

  const openNew = () => { setEditing(null); setForm({ name: "", email: "", password: "", role: "student", nim: "" }); setOpen(true); };
  const openEdit = (u) => { setEditing(u.id); setForm({ name: u.name, email: u.email, password: "", role: u.role, nim: u.nim || "" }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/admin/users/${editing}`, { name: form.name, role: form.role, nim: form.nim || null, password: form.password || undefined });
      } else {
        if (!form.password) { toast.error("Kata sandi wajib untuk pengguna baru"); return; }
        await api.post("/admin/users", { ...form, nim: form.nim || null });
      }
      toast.success("Pengguna disimpan");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => { if (!window.confirm("Hapus pengguna?")) return; try { await api.delete(`/admin/users/${id}`); qc.invalidateQueries({ queryKey: ["admin-users"] }); toast.success("Dihapus"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button onClick={openNew} className="gap-2" data-testid="new-user-button"><Plus className="h-4 w-4" /> Pengguna Baru</Button></div>
      <Card className="rounded-md overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Nama</TableHead><TableHead>Email</TableHead><TableHead>NIM</TableHead><TableHead>Peran</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="font-data text-sm">{u.email}</TableCell>
                <TableCell className="font-data text-sm">{u.nim || "–"}</TableCell>
                <TableCell><Badge variant={u.role === "admin" ? "default" : "outline"}>{ROLE_LABEL[u.role]}</Badge></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(u)}><Pencil className="h-4 w-4" /></Button>
                  {u.id !== me.id && <Button variant="ghost" size="icon" onClick={() => del(u.id)} data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? "Edit Pengguna" : "Pengguna Baru"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label className="text-xs">Nama</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="user-name-input" /></div>
            <div className="space-y-1"><Label className="text-xs">Email</Label><Input type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="user-email-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Peran</Label>
                <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="student">Mahasiswa</SelectItem><SelectItem value="lecturer">Dosen</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label className="text-xs">NIM (opsional)</Label><Input value={form.nim} onChange={(e) => setForm((f) => ({ ...f, nim: e.target.value }))} /></div>
            </div>
            <div className="space-y-1"><Label className="text-xs">Kata Sandi {editing && "(kosongkan jika tidak diubah)"}</Label><Input type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} data-testid="user-password-input" /></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-user-button">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const REF = {
  columns: {
    endpoint: "columns", queryKey: "columns", title: "Kolom",
    cols: ["name", "chemistry"],
    fields: [
      { k: "name", label: "Nama", type: "text" }, { k: "chemistry", label: "Kimia", type: "text" },
      { k: "length_mm", label: "Panjang (mm)", type: "number" }, { k: "id_mm", label: "ID (mm)", type: "number", step: 0.1 },
      { k: "particle_um", label: "Partikel (µm)", type: "number", step: 0.1 }, { k: "porosity", label: "Porositas", type: "number", step: 0.01 },
      { k: "hydrophobicity_factor", label: "Faktor Hidrofobisitas", type: "number", step: 0.01 }, { k: "aromatic_bonus", label: "Bonus Aromatik", type: "number", step: 0.01 },
      { k: "silanol_activity", label: "Aktivitas Silanol", type: "number", step: 0.01 }, { k: "description", label: "Deskripsi", type: "textarea" },
    ],
    empty: { name: "", chemistry: "C18", length_mm: 150, id_mm: 4.6, particle_um: 5, porosity: 0.65, hydrophobicity_factor: 1.0, aromatic_bonus: 0.1, silanol_activity: 0.25, description: "" },
  },
  analytes: {
    endpoint: "analytes", queryKey: "analytes", title: "Analit",
    cols: ["name", "logP"],
    fields: [
      { k: "name", label: "Nama", type: "text" }, { k: "logP", label: "log P", type: "number", step: 0.01 },
      { k: "pka", label: "pKa (kosong jika netral)", type: "number", step: 0.01, nullable: true },
      { k: "acid_base", label: "Sifat", type: "select", options: [["neutral", "Netral"], ["acid", "Asam"], ["base", "Basa"]] },
      { k: "aromatic", label: "Aromatik", type: "switch" }, { k: "lambda_max", label: "λ maks (nm)", type: "number" },
      { k: "response_factor", label: "Faktor Respons", type: "number", step: 0.1 },
    ],
    empty: { name: "", logP: 1.0, pka: null, acid_base: "neutral", aromatic: false, lambda_max: 254, response_factor: 1.0 },
  },
  solvents: {
    endpoint: "solvents", queryKey: "solvents", title: "Pelarut",
    cols: ["name", "type"],
    fields: [
      { k: "name", label: "Nama", type: "text" },
      { k: "type", label: "Jenis", type: "select", options: [["organic", "Organik"], ["aqueous", "Air/Buffer"]] },
      { k: "strength_S", label: "Kekuatan S", type: "number", step: 0.1 }, { k: "viscosity_factor", label: "Faktor Viskositas", type: "number", step: 0.01 },
      { k: "uv_cutoff_nm", label: "UV Cutoff (nm)", type: "number" }, { k: "description", label: "Deskripsi", type: "textarea" },
    ],
    empty: { name: "", type: "organic", strength_S: 3.0, viscosity_factor: 1.0, uv_cutoff_nm: 200, description: "" },
  },
};

function ReferenceTab() {
  return (
    <Tabs defaultValue="columns">
      <TabsList>
        <TabsTrigger value="columns" data-testid="ref-tab-columns">Kolom</TabsTrigger>
        <TabsTrigger value="analytes" data-testid="ref-tab-analytes">Analit</TabsTrigger>
        <TabsTrigger value="solvents" data-testid="ref-tab-solvents">Pelarut</TabsTrigger>
      </TabsList>
      {Object.keys(REF).map((k) => <TabsContent key={k} value={k} className="mt-4"><RefEditor config={REF[k]} /></TabsContent>)}
    </Tabs>
  );
}

function RefEditor({ config }) {
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({ queryKey: [`ref-${config.queryKey}`], queryFn: () => api.get(`/reference/${config.endpoint}`).then((r) => r.data) });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(config.empty);

  const openNew = () => { setEditing(null); setForm(config.empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it.id); setForm({ ...it }); setOpen(true); };

  const save = async () => {
    const payload = { ...form };
    try {
      if (editing) await api.put(`/reference/${config.endpoint}/${editing}`, payload);
      else await api.post(`/reference/${config.endpoint}`, payload);
      toast.success(`${config.title} disimpan`);
      setOpen(false);
      qc.invalidateQueries({ queryKey: [`ref-${config.queryKey}`] });
      qc.invalidateQueries({ queryKey: [config.queryKey] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => { if (!window.confirm("Hapus data ini?")) return; await api.delete(`/reference/${config.endpoint}/${id}`); qc.invalidateQueries({ queryKey: [`ref-${config.queryKey}`] }); qc.invalidateQueries({ queryKey: [config.queryKey] }); toast.success("Dihapus"); };
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button onClick={openNew} className="gap-2" data-testid={`new-${config.endpoint}-button`}><Plus className="h-4 w-4" /> Tambah {config.title}</Button></div>
      <Card className="rounded-md overflow-x-auto">
        <Table>
          <TableHeader><TableRow>{config.cols.map((c) => <TableHead key={c} className="capitalize">{c}</TableHead>)}<TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((it) => (
              <TableRow key={it.id} data-testid={`ref-row-${it.id}`}>
                {config.cols.map((c) => <TableCell key={c} className={c === "name" ? "font-medium" : "font-data text-sm"}>{String(it[c])}</TableCell>)}
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(it)} data-testid={`edit-ref-${it.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => del(it.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Tambah"} {config.title}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {config.fields.map((f) => (
              <div key={f.k} className={`space-y-1 ${f.type === "textarea" ? "col-span-2" : ""}`}>
                <Label className="text-xs">{f.label}</Label>
                {f.type === "text" && <Input value={form[f.k] ?? ""} onChange={(e) => upd(f.k, e.target.value)} data-testid={`field-${f.k}`} />}
                {f.type === "number" && <Input type="number" step={f.step || 1} className="font-data" value={form[f.k] ?? ""} onChange={(e) => upd(f.k, e.target.value === "" ? (f.nullable ? null : 0) : parseFloat(e.target.value))} data-testid={`field-${f.k}`} />}
                {f.type === "textarea" && <Textarea rows={2} value={form[f.k] ?? ""} onChange={(e) => upd(f.k, e.target.value)} />}
                {f.type === "select" && (
                  <Select value={form[f.k]} onValueChange={(v) => upd(f.k, v)}>
                    <SelectTrigger data-testid={`field-${f.k}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{f.options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                {f.type === "switch" && <div className="pt-1"><Switch checked={!!form[f.k]} onCheckedChange={(v) => upd(f.k, v)} data-testid={`field-${f.k}`} /></div>}
              </div>
            ))}
          </div>
          <DialogFooter><Button onClick={save} data-testid={`save-${config.endpoint}-button`}>Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ScoringTab() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["scoring-config"], queryFn: () => api.get("/reference/scoring-config").then((r) => r.data) });
  const [cfg, setCfg] = useState(null);
  React.useEffect(() => { if (data) setCfg(data); }, [data]);
  if (!cfg) return <p className="text-muted-foreground">Memuat…</p>;

  const num = (path, k, v) => setCfg((c) => ({ ...c, [path]: { ...c[path], [k]: parseFloat(v) || 0 } }));

  const save = async () => {
    const w = cfg.weights;
    const sum = w.resolution + w.tailing + w.efficiency + w.parameter;
    if (Math.abs(sum - 1) > 0.001) { toast.error(`Bobot skor harus berjumlah 1.0 (saat ini ${sum.toFixed(2)})`); return; }
    try {
      await api.put("/reference/scoring-config", { weights: cfg.weights, thresholds: cfg.thresholds, leaderboard: cfg.leaderboard });
      toast.success("Konfigurasi skoring disimpan");
      qc.invalidateQueries({ queryKey: ["scoring-config"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const Field = ({ path, k, label, step = 0.05 }) => (
    <div className="space-y-1"><Label className="text-xs">{label}</Label>
      <Input type="number" step={step} className="font-data" value={cfg[path][k]} onChange={(e) => num(path, k, e.target.value)} data-testid={`scoring-${path}-${k}`} /></div>
  );

  return (
    <div className="space-y-4 max-w-2xl">
      <Card className="p-5 rounded-md">
        <h3 className="font-heading font-bold mb-3">Bobot Skor Eksperimen (jumlah = 1.0)</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Field path="weights" k="resolution" label="Resolusi" />
          <Field path="weights" k="tailing" label="Tailing" />
          <Field path="weights" k="efficiency" label="Efisiensi" />
          <Field path="weights" k="parameter" label="Parameter" />
        </div>
      </Card>
      <Card className="p-5 rounded-md">
        <h3 className="font-heading font-bold mb-3">Ambang Validitas</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Field path="thresholds" k="tailing_min" label="Tailing min" step={0.1} />
          <Field path="thresholds" k="tailing_max" label="Tailing maks" step={0.1} />
          <Field path="thresholds" k="resolution_good" label="Rs baik" step={0.1} />
          <Field path="thresholds" k="efficiency_good" label="N baik" step={500} />
        </div>
      </Card>
      <Card className="p-5 rounded-md">
        <h3 className="font-heading font-bold mb-3">Bobot Papan Skor</h3>
        <div className="grid grid-cols-2 gap-3">
          <Field path="leaderboard" k="experiment_weight" label="Bobot Eksperimen" />
          <Field path="leaderboard" k="pjbl_weight" label="Bobot PjBL" />
        </div>
      </Card>
      <Button onClick={save} data-testid="save-scoring-button">Simpan Konfigurasi</Button>
    </div>
  );
}
